/**
 * useUserSearch — React Query hook for `/salescoach/api/users/search` autocomplete
 * (Feature 1 — User filter autocomplete, Plan 04-A).
 *
 * Wire shape (from `src/dashboard/routes/api/users.ts`):
 *   GET /salescoach/api/users/search?q=<substring>&limit=<n>
 *     200 → [{ slackId, username, displayName }]
 *
 * Debounce: 200 ms (spec). The raw `query` from the combobox changes on
 * every keystroke; we push it through a tiny setTimeout-based debounce
 * into `debouncedQuery`, then key the React Query entry off that. This
 * mirrors the pattern used by the existing `use-feedback` hook (server
 * cache is keyed by semantic shape, not by raw URL / keystroke).
 *
 * Bug D15 addendum: browse mode. The backend now treats `q=` (empty) as
 * "return the first N users ordered by username" so the combobox can show
 * a shortlist before the user types. The same endpoint is reused with a
 * larger limit and a longer cache so we don't thrash the backend every
 * time the popover opens.
 */

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { apiFetch } from '@/services/api';

export interface UserSearchRow {
  slackId: string;
  username: string;
  displayName: string;
  /** BUG-4: separate realName so the dropdown can render Name / @handle on
   *  two clean lines (instead of splitting the pre-composed displayName).
   *  Nullable — missing for users without a slack_user_directory entry. */
  realName?: string | null;
}

const DEBOUNCE_MS = 200;
const STALE_TIME_MS = 30_000;
const BROWSE_STALE_TIME_MS = 5 * 60_000;
const DEFAULT_LIMIT = 20;
const BROWSE_LIMIT = 50;

/** Tiny setTimeout-based debounce — isolates the hook from date-fns/lodash. */
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState<T>(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

/**
 * React Query hook that fetches user-search hits for the given query.
 * Returns `{ data, isLoading }` — matches the spec signature. Errors fall
 * back to an empty list (autocomplete failure should degrade gracefully;
 * the combobox shows "no results" rather than a red crash state).
 *
 * Bug D15 — `options.browseWhenEmpty` lets the combobox request a larger
 * "recent users" list when the user just clicks the trigger without
 * typing. Cached separately (5-minute staleTime) since the set is slow-
 * changing; the backend's 50-row cap keeps payloads small.
 */
export function useUserSearch(
  query: string,
  options: {
    browseWhenEmpty?: boolean;
    /**
     * The FilterBar's global Admins control. `'included'` asks the backend to
     * list admin Slack accounts too — the names have to appear exactly when
     * their rows do, or the tables and this filter disagree about who exists.
     *
     * It is part of the query KEY as well as the URL: React Query caches by
     * key, so a shared key would serve the admin-free list back after the
     * toggle moved and the omission would look like "no such user".
     */
    admins?: 'excluded' | 'included';
  } = {},
): {
  data: UserSearchRow[];
  isLoading: boolean;
} {
  const { browseWhenEmpty = false, admins = 'excluded' } = options;
  const debounced = useDebouncedValue(query.trim(), DEBOUNCE_MS);
  const isSearch = debounced.length > 0;
  const enabled = isSearch || browseWhenEmpty;
  // Emitted only off-default, matching `stripDefaults` on the filter URL.
  const adminsParam = admins === 'included' ? `&admins=${admins}` : '';

  const q = useQuery<UserSearchRow[]>({
    queryKey: isSearch
      ? ['users', 'search', debounced, admins]
      : ['users', 'browse', admins],
    queryFn: async () => {
      if (isSearch) {
        const url = `/salescoach/api/users/search?q=${encodeURIComponent(
          debounced,
        )}&limit=${DEFAULT_LIMIT}${adminsParam}`;
        return apiFetch<UserSearchRow[]>(url);
      }
      // Browse mode: empty `q` — the backend skips the WHERE clause and
      // returns the first `BROWSE_LIMIT` users ordered by username. Errors
      // (backend unreachable etc.) still fall back to [] so the combobox
      // shows its "Start typing to search" empty state instead of a crash.
      try {
        return await apiFetch<UserSearchRow[]>(
          `/salescoach/api/users/search?q=&limit=${BROWSE_LIMIT}${adminsParam}`,
        );
      } catch {
        return [];
      }
    },
    enabled,
    staleTime: isSearch ? STALE_TIME_MS : BROWSE_STALE_TIME_MS,
  });

  return {
    data: q.data ?? [],
    // When disabled, React Query reports isLoading=true (no data yet).
    // That leaks "loading" onto the combobox at rest, so flip it to
    // false when we never issued a fetch.
    isLoading: enabled ? q.isLoading : false,
  };
}
